import { useQuery } from '@tanstack/react-query';
import { httpGet } from '$core/helpers/xhr';
import { Product } from '$core/types/Product';
import { useGlobalContext } from '../../contexts';

type UseGetProductsProps = {
  productType: string;
  selectedStrumento: string;
};

const buildStoreFilter = (storeUuid: string | null): string => (
  storeUuid ? `&filter[stores.meta.drupal_internal__target_id]=${storeUuid}` : ''
);

const mapStandardProduct = (item: any): Product => {
  const img: string = item.field_image?.[0]?.uri?.url ?? '';
  const altContent: string = item.field_image?.[0]?.meta?.alt ?? 'alessandro-milani';

  return {
    internalId: item.drupal_internal__product_id,
    name: item.title,
    imageUrl: img,
    price: item.variations?.[0]?.price.number,
    legno: item.field_legno?.name,
    finiture: item.field_finiture?.name,
    strumento: item.field_strumento?.name,
    modello: item.field_modello?.name,
    alt: altContent,
  };
};

const mapMentoniereProduct = (item: any): Product => {
  const img: string = item.field_image?.[0]?.uri?.url ?? '';
  const altContent: string = item.field_image?.[0]?.meta?.alt ?? 'alessandro-milani';

  return {
    internalId: item.drupal_internal__product_id,
    name: item.title,
    imageUrl: img,
    legno: item.field_legno?.name,
    finiture: item.field_finiture?.name,
    strumento: item.field_strumento?.name,
    modello: item.field_modello?.name,
    alt: altContent,
  };
};

const mapServiziProduct = (item: any): Product => {
  const img: string = item.field_image?.[0]?.uri?.url ?? '';
  const altContent: string = item.field_image?.[0]?.meta?.alt ?? 'alessandro-milani';

  return {
    internalId: item.drupal_internal__product_id,
    name: item.title,
    imageUrl: img,
    strumento: item.field_strumento?.name,
    alt: altContent,
  };
};

const mapCordiereCelloProduct = (item: any): Product => {
  const img: string = item.field_image?.[0]?.uri?.url ?? '';
  const altContent: string = item.field_image?.[0]?.meta?.alt ?? 'alessandro-milani';

  return {
    internalId: item.drupal_internal__product_id,
    name: item.title,
    imageUrl: img,
    legno: item.field_legno?.name,
    strumento: item.field_strumento?.name,
    alt: altContent,
  };
};

const mapVitiProduct = (item: any): Product => {
  const img: string = item.field_immagini?.[0]?.field_media_image?.uri?.url ?? '';
  const altContent: string = item.field_media_image?.[0]?.meta?.alt ?? 'alessandro-milani';

  return {
    internalId: item.drupal_internal__product_id,
    name: item.title,
    imageUrl: img,
    price: item.variations?.[0]?.price.number,
    alt: altContent,
    metallo: item.field_metallo?.name,
    strumento: item.field_strumento?.name,
    modello: item.field_modello?.name,
  };
};

const buildProductUrl = (
  productType: string,
  selectedStrumento: string,
  storeFilter: string,
): string => {
  if (productType === 'piroli' || productType === 'speciali') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_legno,field_modello,field_strumento,field_finiture'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'cordiere' && selectedStrumento !== 'violoncello') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_legno,field_modello,field_strumento,field_finiture'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'cordiere' && selectedStrumento === 'violoncello') {
    return '/commerce_product/cordiera_cello'
      + '?include=field_image,variations,field_legno, field_strumento'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + '&filter[langcode]=it'
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'mentoniere') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_legno, field_modello, field_strumento'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + '&filter[langcode]=it'
      + `&filter[status]=1${storeFilter}`;
  }

  if (productType === 'servizi') {
    return `/commerce_product/${productType}`
      + '?include=field_image,variations,field_strumento'
      + `&filter[field_strumento.name]=${selectedStrumento}`
      + '&filter[langcode]=it'
      + `&filter[status]=1${storeFilter}`;
  }

  return '/commerce_product/viti'
    + '?include=field_immagini,field_immagini.field_media_image,variations,field_metallo,field_strumento,field_modello'
    + `&filter[field_strumento.name]=${selectedStrumento}`
    + '&filter[langcode]=it'
    + `&filter[status]=1${storeFilter}`;
};

const mapProducts = (productType: string, selectedStrumento: string, data: any[]): Product[] => {
  if (productType === 'piroli' || productType === 'speciali') return data.map(mapStandardProduct);
  if (productType === 'cordiere' && selectedStrumento !== 'cello') return data.map(mapStandardProduct);
  if (productType === 'cordiere' && selectedStrumento === 'cello') return data.map(mapCordiereCelloProduct);
  if (productType === 'mentoniere') return data.map(mapMentoniereProduct);
  if (productType === 'servizi') return data.map(mapServiziProduct);
  return data.map(mapVitiProduct);
};

const useGetProducts = ({ productType, selectedStrumento }: UseGetProductsProps) => {
  const { storeUuid } = useGlobalContext();
  const storeFilter = buildStoreFilter(storeUuid);

  const { data, isLoading } = useQuery({
    queryKey: ['products', productType, selectedStrumento, storeUuid],
    queryFn: async () => {
      const url = buildProductUrl(productType, selectedStrumento, storeFilter);
      const response = await httpGet(url);
      const res = response.data?.data;
      if (!Array.isArray(res)) return [];
      return mapProducts(productType, selectedStrumento, res);
    },
    enabled: !!storeUuid && !!selectedStrumento,
  });

  return { product: data ?? [], loading: isLoading };
};

export { useGetProducts };
